import { API_HOST } from '@/constants/config';

// SSR-safe task-template API client used by `generateStaticParams` and other
// build-time SEO paths. Mirrors `lib/blogApi.js` / `lib/toolApi.js` —
// direct fetch (no auth, no Zustand) so it works inside Node during the
// Expo Router static export.
//
// Runtime/auth-bound calls still live in `lib/api.js` (`taskTemplatesApi`).

const toQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.set(key, String(value));
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

const fetchTemplates = async (path, params = {}) => {
  const url = `${API_HOST}${path}${toQueryString(params)}`;
  let response;

  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Platform': 'web',
      },
    });
  } catch (networkErr) {
    throw new Error('Network error — unable to reach the task templates service.');
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(
      response.ok
        ? 'Task templates service returned an unexpected response.'
        : `Task templates service returned ${response.status}.`,
    );
  }

  const data = await response.json();
  if (!response.ok || data?.error) {
    throw new Error(data?.message || 'Failed to fetch task templates data');
  }

  return data;
};

export const taskTemplateApi = {
  // Public list of enabled task templates. Used at build-time for
  // `generateStaticParams` on /tasks/template/[slug] and to render the
  // catalog SEO body.
  listTemplates: async ({ category, featured } = {}) => {
    const data = await fetchTemplates('/json/task-templates', { category, featured });
    return {
      templates: data.templates || [],
      categories: data.categories || [],
    };
  },

  // Single task template by slug — public detail with configurable
  // fields, default schedule, and SEO meta.
  getTemplateBySlug: async (slug) => {
    if (!slug) return null;
    const data = await fetchTemplates(`/json/task-templates/${encodeURIComponent(slug)}`);
    return data.template || null;
  },
};
